import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { products } from '@/mocks/products';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const TABS = ['Overview', 'Ingredients', 'Nutrition', 'How to Use'] as const;
type Tab = typeof TABS[number];

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const product = products.find((p) => p.id === id);

  const [activeImage, setActiveImage] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const [quantity, setQuantity] = useState(1);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!product) return;
    setActiveImage(product.image);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-product-detail';
    const schemaObj: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: 'Products', item: `${SITE_URL}/products` },
            { '@type': 'ListItem', position: 3, name: product.category, item: `${SITE_URL}/products?category=${product.category}` },
            { '@type': 'ListItem', position: 4, name: product.name, item: `${SITE_URL}/products/${product.id}` },
          ],
        },
        {
          '@type': 'Product',
          '@id': `${SITE_URL}/products/${product.id}#product`,
          name: product.name,
          description: product.details || product.description,
          image: product.images && product.images.length > 0 ? product.images : [product.image],
          sku: `FAASLEY-${product.id}`,
          brand: { '@type': 'Brand', name: product.brand },
          category: product.category,
          url: `${SITE_URL}/products/${product.id}`,
          ...(product.weight ? { weight: { '@type': 'QuantitativeValue', name: product.weight } } : {}),
          ...(product.ingredients && product.ingredients.length > 0 ? { material: product.ingredients.join(', ') } : {}),
          offers: {
            '@type': 'Offer',
            availability: 'https://schema.org/InStock',
            priceCurrency: 'INR',
            seller: {
              '@type': 'Organization',
              name: 'Faasley Foods',
              url: SITE_URL,
            },
            shippingDetails: {
              '@type': 'OfferShippingDetails',
              shippingRate: { '@type': 'MonetaryAmount', currency: 'INR' },
              shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'IN' },
            },
          },
          ...(product.nutritionFacts && product.nutritionFacts.length > 0 ? {
            nutrition: {
              '@type': 'NutritionInformation',
              servingSize: product.weight || '100g',
              calories: product.nutritionFacts.find((n) => n.label === 'Energy')?.value || '',
            },
          } : {}),
        },
      ],
    };
    script.textContent = JSON.stringify(schemaObj);
    document.head.appendChild(script);
    document.title = `${product.name} | ${product.brand} ${product.category} — Faasley Foods`;

    return () => {
      const el = document.getElementById('schema-product-detail');
      if (el) el.remove();
    };
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center py-32">
          <div className="w-16 h-16 flex items-center justify-center bg-amber-50 rounded-full mb-5">
            <i className="ri-search-line text-amber-500 text-2xl"></i>
          </div>
          <h2 className="font-display text-2xl font-bold text-stone-900 mb-2">Product not found</h2>
          <p className="text-stone-400 text-sm mb-6">This product doesn&apos;t exist or may have been removed.</p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 bg-stone-900 text-white font-semibold px-6 py-3 rounded-full text-sm transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i> Back to Products
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const gallery = product.images && product.images.length > 1 ? product.images : [product.image];
  const relatedProducts = products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);

  const whatsappMsg = encodeURIComponent(
    `Hi Faasley Foods! I'm interested in ordering:\n\n*${product.name}* (${product.brand})\nWeight: ${product.weight || 'N/A'}\nQty: ${quantity}\n\nPlease share availability and pricing. Thank you!`
  );

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-[120px] md:pt-[140px] pb-4 border-b border-stone-100">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="flex items-center gap-2 text-xs text-stone-400 flex-wrap">
            <Link to="/" className="hover:text-amber-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/products" className="hover:text-amber-600 transition-colors">Products</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/products?category=${product.category}`} className="hover:text-amber-600 transition-colors">{product.category}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-stone-600 font-medium">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Main Product Section */}
      <section className="py-10 md:py-16">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">

            {/* ── LEFT: Image Gallery ── */}
            <div className="flex flex-col gap-4">
              {/* Main Image */}
              <div className="relative rounded-2xl overflow-hidden bg-stone-50 aspect-square border border-stone-100">
                <img
                  src={activeImage}
                  alt={product.name}
                  className="w-full h-full object-cover transition-all duration-500"
                />
                {product.badge && (
                  <span className="absolute top-4 left-4 bg-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                    {product.badge}
                  </span>
                )}
                {/* Share button */}
                <button
                  onClick={handleShare}
                  className="absolute top-4 right-4 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center text-stone-500 hover:text-amber-600 transition-colors cursor-pointer"
                  title="Copy link"
                >
                  <i className={copied ? 'ri-check-line text-green-600' : 'ri-share-line'}></i>
                </button>
                {copied && (
                  <div className="absolute top-14 right-4 bg-stone-900 text-white text-xs px-3 py-1.5 rounded-full">
                    Link copied!
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {gallery.length > 1 && (
                <div className="flex gap-2.5 overflow-x-auto pb-1">
                  {gallery.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImage(img)}
                      className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden transition-all cursor-pointer"
                      style={{
                        border: activeImage === img ? '2px solid #f59e0b' : '2px solid #e7e5e4',
                        opacity: activeImage === img ? 1 : 0.65,
                      }}
                    >
                      <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {/* Trust badges row */}
              <div className="grid grid-cols-3 gap-3 mt-1">
                {[
                  { icon: 'ri-shield-check-line', label: 'FSSAI Certified' },
                  { icon: 'ri-truck-line', label: 'Pan-India Delivery' },
                  { icon: 'ri-refresh-line', label: '7-Day Returns' },
                ].map((b) => (
                  <div key={b.label} className="flex flex-col items-center gap-1.5 bg-stone-50 rounded-xl py-3 px-2 border border-stone-100">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className={`${b.icon} text-amber-500 text-base`}></i>
                    </div>
                    <span className="text-[10px] text-stone-500 text-center font-medium leading-tight">{b.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── RIGHT: Product Info ── */}
            <div className="flex flex-col gap-6">
              {/* Brand + Name */}
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-amber-600">{product.brand}</span>
                  <span className="text-stone-200">|</span>
                  <span className="text-xs text-stone-400">{product.category}</span>
                </div>
                <h1 className="font-display text-3xl md:text-4xl font-bold text-stone-900 leading-tight mb-3">
                  {product.name}
                </h1>
                <p className="text-stone-500 text-base leading-relaxed">
                  {product.details || product.description}
                </p>
              </div>

              {/* Tags */}
              {product.tags && product.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {product.tags.map((tag) => (
                    <span key={tag} className="bg-amber-50 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full border border-amber-100">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Meta info */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: 'ri-scales-line', label: 'Net Weight', value: product.weight || 'N/A' },
                  { icon: 'ri-map-pin-line', label: 'Origin', value: product.origin || 'India' },
                  { icon: 'ri-time-line', label: 'Shelf Life', value: product.shelfLife || 'See packaging' },
                  { icon: 'ri-shield-check-line', label: 'Certification', value: 'FSSAI Certified' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3 bg-stone-50 rounded-xl p-3.5 border border-stone-100">
                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-amber-500 text-sm`}></i>
                    </div>
                    <div>
                      <p className="text-[10px] text-stone-400 uppercase tracking-wider mb-0.5">{item.label}</p>
                      <p className="text-xs font-semibold text-stone-800">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quantity + Order */}
              <div className="pt-2">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Quantity</p>
                <div className="flex items-center gap-3 mb-5">
                  <div className="flex items-center border border-stone-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-10 h-10 flex items-center justify-center text-stone-500 hover:bg-stone-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="w-10 text-center font-semibold text-stone-900 text-sm">{quantity}</span>
                    <button
                      onClick={() => setQuantity((q) => q + 1)}
                      className="w-10 h-10 flex items-center justify-center text-stone-500 hover:bg-stone-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                  <span className="text-xs text-stone-400">Min. order: 1 unit</span>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={`https://wa.me/918800889959?text=${whatsappMsg}`}
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2.5 font-bold py-4 rounded-xl transition-all whitespace-nowrap text-sm cursor-pointer"
                    style={{ background: '#25D366', color: '#fff' }}
                  >
                    <i className="ri-whatsapp-line text-lg"></i>
                    Order on WhatsApp
                  </a>
                  <Link
                    to="/contact"
                    className="flex-1 inline-flex items-center justify-center gap-2.5 font-semibold py-4 rounded-xl transition-all whitespace-nowrap text-sm cursor-pointer border border-stone-200 text-stone-700 hover:bg-stone-50"
                  >
                    <i className="ri-mail-send-line"></i>
                    Send Enquiry
                  </Link>
                </div>

                <p className="text-xs text-stone-400 text-center mt-3">
                  <i className="ri-lock-line mr-1"></i>
                  Secure enquiry · We respond within 24 hours
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Tabs Section ── */}
      <section className="py-10 md:py-14 bg-stone-50 border-t border-stone-100">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          {/* Tab Nav */}
          <div className="flex gap-1 bg-white rounded-xl p-1 border border-stone-100 w-fit mb-10 flex-wrap">
            {TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
                style={
                  activeTab === tab
                    ? { background: '#1c1917', color: '#fff' }
                    : { color: '#78716c' }
                }
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="max-w-3xl">
            {activeTab === 'Overview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-display text-xl font-bold text-stone-900 mb-4">Key Benefits</h3>
                  {product.benefits && product.benefits.length > 0 ? (
                    <ul className="space-y-3">
                      {product.benefits.map((b, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="w-6 h-6 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-amber-600 text-xs"></i>
                          </div>
                          <span className="text-stone-600 text-sm leading-relaxed">{b}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-stone-400 text-sm">{product.details || product.description}</p>
                  )}
                </div>
                {product.origin && (
                  <div className="bg-white rounded-2xl p-6 border border-stone-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-map-pin-line text-amber-500 text-base"></i>
                      </div>
                      <h4 className="font-semibold text-stone-900 text-sm">Sourced From</h4>
                    </div>
                    <p className="text-stone-500 text-sm pl-11">{product.origin}</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'Ingredients' && (
              <div>
                <h3 className="font-display text-xl font-bold text-stone-900 mb-4">Ingredients</h3>
                {product.ingredients && product.ingredients.length > 0 ? (
                  <div className="flex flex-wrap gap-2.5">
                    {product.ingredients.map((ing, i) => (
                      <span
                        key={i}
                        className="bg-white border border-stone-200 text-stone-700 text-sm px-4 py-2 rounded-full font-medium"
                      >
                        {ing}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-stone-400 text-sm">Ingredient information not available.</p>
                )}
                <div className="mt-6 bg-amber-50 rounded-2xl p-5 border border-amber-100">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                      <i className="ri-information-line text-amber-600 text-base"></i>
                    </div>
                    <p className="text-xs text-amber-800 leading-relaxed">
                      All Faasley products are FSSAI certified. No artificial colors, flavors, or preservatives are used unless explicitly listed. Always check the packaging for the most up-to-date ingredient information.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'Nutrition' && (
              <div>
                <h3 className="font-display text-xl font-bold text-stone-900 mb-4">Nutrition Facts</h3>
                {product.nutritionFacts && product.nutritionFacts.length > 0 ? (
                  <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                    <div className="bg-stone-900 px-6 py-4">
                      <p className="text-white font-bold text-lg">Nutrition Information</p>
                      <p className="text-stone-400 text-xs mt-0.5">Per serving / as indicated</p>
                    </div>
                    <div className="divide-y divide-stone-100">
                      {product.nutritionFacts.map((fact, i) => (
                        <div key={i} className="flex justify-between items-center px-6 py-3.5">
                          <span className="text-stone-600 text-sm">{fact.label}</span>
                          <span className="font-semibold text-stone-900 text-sm">{fact.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="px-6 py-4 bg-stone-50 border-t border-stone-100">
                      <p className="text-xs text-stone-400">* Values are approximate and may vary slightly by batch. Refer to product packaging for exact values.</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-stone-400 text-sm">Nutrition information not available.</p>
                )}
              </div>
            )}

            {activeTab === 'How to Use' && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-display text-xl font-bold text-stone-900 mb-4">How to Use</h3>
                  {product.howToUse ? (
                    <div className="bg-white rounded-2xl p-6 border border-stone-100">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl flex-shrink-0">
                          <i className="ri-lightbulb-line text-amber-500 text-lg"></i>
                        </div>
                        <p className="text-stone-600 text-sm leading-relaxed">{product.howToUse}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-stone-400 text-sm">Usage information not available.</p>
                  )}
                </div>
                {product.shelfLife && (
                  <div className="bg-white rounded-2xl p-6 border border-stone-100">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl flex-shrink-0">
                        <i className="ri-time-line text-amber-500 text-lg"></i>
                      </div>
                      <div>
                        <p className="font-semibold text-stone-900 text-sm mb-1">Shelf Life &amp; Storage</p>
                        <p className="text-stone-500 text-sm leading-relaxed">{product.shelfLife}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Related Products ── */}
      {relatedProducts.length > 0 && (
        <section className="py-14 md:py-20 bg-white border-t border-stone-100">
          <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
            <div className="flex items-center justify-between mb-8">
              <div>
                <p className="text-amber-600 text-xs font-bold uppercase tracking-widest mb-1">More from {product.category}</p>
                <h2 className="font-display text-2xl font-bold text-stone-900">You May Also Like</h2>
              </div>
              <Link
                to="/products"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-stone-500 hover:text-amber-600 transition-colors whitespace-nowrap"
              >
                View All <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {relatedProducts.map((p) => (
                <Link
                  key={p.id}
                  to={`/products/${p.id}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-amber-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative aspect-square overflow-hidden bg-stone-50">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {p.badge && (
                      <span className="absolute top-2.5 left-2.5 bg-amber-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
                        {p.badge}
                      </span>
                    )}
                  </div>
                  <div className="p-3.5">
                    <p className="text-amber-600 text-[10px] font-bold uppercase tracking-wider mb-0.5">{p.brand}</p>
                    <h3 className="font-semibold text-stone-900 text-sm leading-snug line-clamp-1 mb-1">{p.name}</h3>
                    <p className="text-stone-400 text-xs line-clamp-1">{p.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Bottom CTA ── */}
      <section className="py-14 bg-amber-50 border-t border-amber-100">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-2xl font-bold text-stone-900 mb-1">Ready to order?</h3>
              <p className="text-stone-500 text-sm">Message us on WhatsApp for quick ordering, bulk pricing, or any questions.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={`https://wa.me/918800889959?text=${whatsappMsg}`}
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center justify-center gap-2 font-bold px-8 py-3.5 rounded-xl text-sm whitespace-nowrap cursor-pointer transition-all"
                style={{ background: '#25D366', color: '#fff' }}
              >
                <i className="ri-whatsapp-line text-base"></i>
                Order on WhatsApp
              </a>
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center justify-center gap-2 font-semibold px-8 py-3.5 rounded-xl text-sm whitespace-nowrap cursor-pointer border border-stone-200 text-stone-700 hover:bg-white transition-all"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Products
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
